import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideAppInitializer,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
// import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { NgxEchartsModule } from 'ngx-echarts';
import { authorizationInterceptor } from '@core/interceptors/authorization/authorization.interceptor';
import { httpResponseInterceptor } from '@core/interceptors/http-response/http-response.interceptor';
import { localeInterceptor } from '@core/interceptors/locale/locale.interceptor';
import { appInfoInterceptor } from '@core/interceptors/app-info/app-info.interceptor';
import { TrimNumber } from '@shared/pipes/number/trim-number.pipe';
import { BrowserStorageService } from '@core/services/browser-storage/browser-storage.service';
import { GlobalErrorHandler } from '@core/services/error-handler/global-error-handler.service';
import { routes } from './app.routes';
import { ErrorHandler } from '@angular/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { environment } from '@env/environment';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { ThemeService } from '@core/services/theme/theme.service';
import { LanguageService } from '@core/services/language/language.service';
import { firstValueFrom } from 'rxjs';
import { providePrimeNG } from 'primeng/config';
import { BluePreset } from '@core/config/primeng-theme.config';
import { PlatformService } from '@core/services/platform/platform.service';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { AppVersionGateService } from '@core/services/app-version-gate/app-version-gate.service';
import { HomeService } from '@shared/services/home/home.service';
import { VersionCompareUtil } from '@shared/utils/version-compare.util';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/');
}

async function initializeSocialLogin() {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
  const platformService = inject(PlatformService);
  await SocialLogin.initialize({
    google: {
      webClientId: environment.GOOGLE_CLIENT_ID,
      iOSClientId: environment.GOOGLE_IOS_CLIENT_ID,
      iOSServerClientId: environment.GOOGLE_CLIENT_ID,
    },
    ...(!platformService.isAndroid() && {
      apple: {
        clientId: 'ai.stoqa.app',
      },
    }),
  });
}

async function initializeUserProfile() {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
  const browserStorageService = inject(BrowserStorageService);
  const authService = inject(AuthenticationService);
  const themeService = inject(ThemeService);
  const languageService = inject(LanguageService);

  const accessToken = browserStorageService.get('accessToken');
  if (accessToken) {
    try {
      // Load user profile first
      await firstValueFrom(authService.getUserProfile());
      // After user profile is loaded, initialize theme and language
      // The services will use backend configuration since user is now loaded
      themeService.initializeTheme();
      await languageService.initializeLanguage();
    } catch (error) {
      // If profile fetch fails, the interceptor will handle logout/redirect
      console.error('Failed to restore user profile:', error);
      // Initialize from localStorage as fallback
      themeService.initializeTheme();
      await languageService.initializeLanguage();
    }
  } else {
    // No access token - user is not logged in, initialize from localStorage
    themeService.initializeTheme();
    await languageService.initializeLanguage();
  }
}

async function initializeAnalytics() {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
  inject(AnalyticsService).init();
}

async function initializeVersionGate() {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
  const platformService = inject(PlatformService);
  const gateService = inject(AppVersionGateService);
  const homeService = inject(HomeService);
  if (platformService.isWeb()) return;
  await platformService.initializeAppVersion();
  const currentVersion = platformService.appVersion();
  if (!currentVersion) return; // fail open - can't read the native app version
  const platform = platformService.getPlatform() as 'ios' | 'android';
  const entries = await firstValueFrom(homeService.getMinimumAppVersion(platform));
  if (!entries) return; // request failed - fail open
  const match = entries.find((entry) => entry.platform === platform);
  if (!match?.minVersion) return; // no minimum set - let the user through
  if (VersionCompareUtil.isLower(currentVersion, match.minVersion)) {
    gateService.setBlocked(true);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withViewTransitions(),
      withComponentInputBinding()
    ),
    provideClientHydration(withEventReplay()),
    provideHttpClient(
      withInterceptors([
        authorizationInterceptor,
        localeInterceptor,
        appInfoInterceptor,
        httpResponseInterceptor,
      ]),
      withFetch(),
      withInterceptorsFromDi() // DI-based interceptors must be explicitly enabled.
    ),
    provideAnimationsAsync(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'ar',
      }),
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      })
    ),
    BrowserStorageService,
    TrimNumber,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    // These initializers run concurrently (Angular awaits them via Promise.all),
    // not in this array order - none of them depends on another having finished first.
    provideAppInitializer(initializeAnalytics),
    provideAppInitializer(initializeVersionGate),
    provideAppInitializer(initializeSocialLogin),
    provideAppInitializer(initializeUserProfile),
    providePrimeNG({
      theme: {
        preset: BluePreset,
        options: {
          darkModeSelector: '[data-theme="dark"]',
        },
      },
    }),
  ],
};
